"use client";

import { forwardRef, useRef, useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import type { CSSProperties } from "react";

import Image from "next/image";
import Link from "next/link";
import specImage from "@/assets/c9748796f840b58c67ed6f72283eee2a7bc2e017.png";
import { SectionBlock } from "@/components/blocks/SectionBlock";
import { ContactForm } from "@/components/blocks/ContactForm";
import { useIsMobile } from "@/components/ui/use-mobile";
import { useScrollReveal, type RevealState } from "@/shared/lib/useScrollReveal";

import type { Specialist } from "@/entities/specialist";

// ── Хелперы стилей анимации ────────────────────────────────

/** Заголовок секции: одним блоком сверху вниз, DOM: p → h2 (issue 1) */
function headerAnim(state: RevealState): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(-2.5rem)" };
  return {
    opacity: 0,
    animation: "slide-from-top 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s forwards",
  };
}

/**
 * Левая панель — появляется первой при попадании ПАНЕЛЕЙ в viewport.
 * slide-from-bottom без delay, чтобы пользователь чётко видел движение.
 */
function leftPanelAnim(state: RevealState): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(2.5rem)" };
  return {
    opacity: 0,
    animation: "slide-from-bottom 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s forwards",
  };
}

/**
 * Правая панель — появляется после левой с задержкой 0.65s.
 */
function rightPanelAnim(state: RevealState): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(2.5rem)" };
  return {
    opacity: 0,
    animation: "slide-from-bottom 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.65s forwards",
  };
}

/**
 * Тег — эффект деления: поочерёдно вылетают из одного источника.
 * Анимируем ОДИН РАЗ за сессию (animDone = ref, переключается после первого рендера).
 */
function tagStyle(state: RevealState, index: number, animDone: boolean): CSSProperties {
  if (state === "done" || animDone) return {};
  if (state === "idle") return { opacity: 0, transform: "scale(0.55) translateX(-8px)" };
  const delay = 0.55 + index * 0.07;
  return {
    opacity: 0,
    animation: `tag-pop-in 0.28s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay.toFixed(2)}s forwards`,
  };
}

// ── Компонент ──────────────────────────────────────────────

interface SpecialistSectionProps {
  specialists: Specialist[];
}

export const SpecialistSection = forwardRef<HTMLElement, SpecialistSectionProps>(({ specialists }, ref) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showAllTags, setShowAllTags] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(5);
  const trackRef = useRef<HTMLElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const contactFormRef = useRef<HTMLDivElement>(null);
  const [preselectedSpecialist, setPreselectedSpecialist] = useState<string>("");
  const searchParams = useSearchParams();
  const router = useRouter();

  // Read ?specialist=NAME from URL (set by modal "Записаться" link) and pre-fill form
  useEffect(() => {
    const name = searchParams.get("specialist");
    if (!name) return;
    setPreselectedSpecialist(decodeURIComponent(name));
    // Clean up URL param without re-navigating (replace history entry)
    const url = new URL(window.location.href);
    url.searchParams.delete("specialist");
    router.replace(url.pathname + url.hash, { scroll: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cardsRef = useRef<{ [key: string]: HTMLElement | null }>({});
  const isMobile = useIsMobile();
  const prevVisibleCount = useRef(visibleCount);

  // ── Анимация появления заголовка (секция начинает заходить) ──
  const { ref: revealRef, state } = useScrollReveal("specialists", 0.1);

  // ── Анимация панелей (threshold 0.15 — работает и на мобиле, issue 6) ──
  const { ref: panelsRevealRef, state: panelsState } = useScrollReveal(
    "specialists_panels",
    0.15,
  );

  // ── Анимация карусели (карточки в viewport, threshold 0.2) ──
  const { ref: carouselRevealRef, state: carouselState } = useScrollReveal(
    "specialists_carousel",
    0.2,
  );

  /**
   * После первого рендера с panelsState === 'animating' флаг устанавливается в true.
   * useRef не вызывает ре-рендер → CSS анимация успевает запуститься,
   * при следующих ре-рендерах (toggle тегов) — tagStyle возвращает {}, повтора нет.
   */
  const tagsAnimatedRef = useRef(false);
  useEffect(() => {
    if (panelsState === "animating") {
      tagsAnimatedRef.current = true;
    }
  }, [panelsState]);

  useEffect(() => {
    if (
      isMobile &&
      carouselRef.current &&
      visibleCount > prevVisibleCount.current
    ) {
      const firstNewCardIndex = prevVisibleCount.current;
      const targetCard = carouselRef.current.children[
        firstNewCardIndex
      ] as HTMLElement;
      const firstCard = carouselRef.current.children[0] as HTMLElement;

      if (targetCard && firstCard) {
        const targetScroll = targetCard.offsetLeft - firstCard.offsetLeft;

        setTimeout(() => {
          carouselRef.current?.scrollTo({
            left: targetScroll,
            behavior: "instant",
          });
        }, 100);
      }
    }
    prevVisibleCount.current = visibleCount;
  }, [visibleCount, isMobile]);

  const handleScroll = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 5);

      const firstCard = carouselRef.current.children[0] as HTMLElement;
      const secondCard = carouselRef.current.children[1] as HTMLElement;
      if (firstCard && secondCard) {
        const cardWidth = secondCard.offsetLeft - firstCard.offsetLeft;
        const index = Math.round(scrollLeft / cardWidth);
        setCurrentIndex(index);

        const totalCards = carouselRef.current.children.length;
        setCanScrollRight(
          index < totalCards - 1 &&
            Math.ceil(scrollLeft + clientWidth) < scrollWidth - 40,
        );
      } else if (firstCard) {
        setCanScrollRight(
          Math.ceil(scrollLeft + clientWidth) < scrollWidth - 40,
        );
      } else {
        setCanScrollRight(false);
      }
    }
  };

  useEffect(() => {
    handleScroll();
  }, []);

  useEffect(() => {
    try {
      const storedTags = localStorage.getItem("selectedSpecialistTags");
      if (storedTags) {
        setSelectedTags(JSON.parse(storedTags));
      }
    } catch (error) {
      console.error("Failed to parse tags from localStorage", error);
      localStorage.removeItem("selectedSpecialistTags");
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        "selectedSpecialistTags",
        JSON.stringify(selectedTags),
      );
    } catch (error) {
      console.error("Failed to save tags to localStorage", error);
    }
  }, [selectedTags]);

  const allTags = Array.from(
    new Set(
      specialists.flatMap((spec) => [...spec.requests, ...spec.approaches]),
    ),
  );

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const filteredSpecialists =
    selectedTags.length > 0
      ? specialists.filter(
          (spec) =>
            spec.requests.some((r) => selectedTags.includes(r)) ||
            spec.approaches.some((a) => selectedTags.includes(a)),
        )
      : specialists;

  const specialistsToShow =
    selectedTags.length === 0
      ? specialists.slice(0, visibleCount)
      : filteredSpecialists;

  const scrollToCarousel = () => {
    carouselRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToContactFormWith = (specialistName: string) => {
    setPreselectedSpecialist(specialistName);
    contactFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const scrollToContactForm = () => {
    contactFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <section
      ref={(node) => {
        (trackRef as React.MutableRefObject<HTMLElement | null>).current = node;
        revealRef(node);
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLElement | null>).current = node;
        }
      }}
      id="specialists"
      className="relative w-full h-auto flex flex-col gap-y-tile"
    >
      {/* ── Заголовок: одним блоком сверху ── */}
      <div className="overflow-hidden">
        <SectionBlock className="text-left p-fluid-xl">
          <div style={headerAnim(state)}>
            <h2 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground block select-none">
              Эффективные методы <span className="text-help">и тёплые отношения</span>
            </h2>
            <p className="font-content font-light text-fluid-xl leading-relaxed text-foreground/80 mt-fluid-sm block select-none max-w-[65ch]">
              Мы — опытные специалисты в доказательных подходах. Мы опираемся на протоколы, но в центре для нас всегда остаются живые люди и доверительные отношения.
            </p>
          </div>
        </SectionBlock>
      </div>

      <div className="flex flex-col gap-y-tile">
        {/*
          items-stretch (дефолт): оба блока одинаковой высоты = высота более высокого.
          Теги внутри получают min-h-0 на flex-1 контейнере — корректный overflow scroll
          без роста панели за рамки grid-row-height.
          ref для IntersectionObserver с threshold 0.5.
        */}
        <div
          ref={(el) => panelsRevealRef(el as HTMLElement | null)}
          className="grid grid-cols-1 lg:grid-cols-2 gap-tile"
        >
          {/* ── Левая панель (фильтр): первой появляется ── */}
          <div
            className="solid-panel p-fluid-xl relative overflow-hidden flex flex-col min-h-[clamp(400px,80svh,900px)]"
            style={{ border: "1px solid var(--help)", ...leftPanelAnim(panelsState) }}
          >
              <div className="flex flex-col gap-fluid-sm mb-fluid-xl">
                <h3 className="font-heading text-fluid-xl text-foreground leading-tight">
                  Подберите специалиста
                </h3>
                <p className="font-content text-fluid-base text-foreground/80 leading-relaxed mt-fluid-sm">
                  Если вы знаете свой диагноз или сформулировали свой запрос,
                  выберите соответствующие теги, и мы предложим подходящих
                  специалистов.
                </p>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar -mx-fluid-xl px-fluid-xl">
                <div className="flex flex-wrap gap-2 pb-fluid-md">
                  {(showAllTags ? allTags : allTags.slice(0, 8)).map((tag, index) => {
                    const isSelected = selectedTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        // Тег анимируется один раз: split-pop при первом появлении.
                        // tagsAnimatedRef.current блокирует повтор при toggle тегов.
                        style={selectedTags.length === 0 ? tagStyle(panelsState, index, tagsAnimatedRef.current) : undefined}
                        className={`px-4 py-2 rounded-full border font-content text-sm transition-colors ${
                          isSelected
                            ? "bg-help text-white border-help"
                            : "bg-transparent text-foreground border-border lg:hover:border-help"
                        }`}
                      >
                        {tag}
                      </button>
                    );
                  })}
                  {!showAllTags && allTags.length > 8 && (
                    <button
                      onClick={() => setShowAllTags(true)}
                      className="px-4 py-2 rounded-full border border-dashed border-[var(--help)] bg-transparent text-help font-content text-sm transition-colors lg:hover:border-solid lg:hover:bg-help/10"
                    >
                      Показать все
                    </button>
                  )}
                  {showAllTags && allTags.length > 8 && (
                    <button
                      onClick={() => setShowAllTags(false)}
                      className="px-4 py-2 rounded-full border border-dashed border-[#FF5E0E] bg-transparent text-[#FF5E0E] font-content text-sm transition-colors lg:hover:border-solid lg:hover:bg-[#FF5E0E]/10"
                    >
                      Свернуть
                    </button>
                  )}
                </div>
              </div>

              {selectedTags.length > 0 && (
                <div className="py-fluid-md">
                  <button onClick={scrollToCarousel} className="btn-solid w-full">
                    Показать
                  </button>
                </div>
              )}

              <div className="mt-auto pt-fluid-xl border-t border-border/50 flex justify-between items-center">
                {selectedTags.length > 0 ? (
                  <span className="text-sm text-muted-foreground">
                    Найдено специалистов: {filteredSpecialists.length}
                  </span>
                ) : (
                  <span />
                )}
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className="h-[44px] rounded-full border-[1.5px] px-6 flex items-center justify-center font-content font-semibold text-[var(--fluid-text-sm)] tracking-wide cursor-pointer transition-all duration-300 select-none whitespace-nowrap bg-transparent !text-[#FF5E0E] border-[#FF5E0E] lg:hover:bg-[#FF5E0E] lg:hover:!text-white"
                  >
                    Сбросить
                  </button>
                )}
              </div>
          </div>

          {/* ── Правая панель (форма): независимая высота, то же min-h ── */}
          <div
            ref={contactFormRef}
            id="contact-form"
            className="solid-panel p-fluid-xl relative flex flex-col min-h-[clamp(400px,80svh,900px)]"
            style={{ border: "1px solid var(--help)", ...rightPanelAnim(panelsState) }}
          >
            <div className="flex flex-col gap-fluid-sm mb-fluid-xl">
              <h3 className="font-heading text-fluid-xl text-foreground leading-tight">
                Или доверьте выбор нам
              </h3>
              <p className="font-content text-fluid-base text-foreground/80 leading-relaxed mt-fluid-sm">
                Оставьте свои контакты, и наш администратор свяжется с вами,
                чтобы помочь подобрать подходящего специалиста.
              </p>
            </div>
            <div className="flex-grow flex flex-col">
              <ContactForm preselectedSpecialist={preselectedSpecialist} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Карусель ── */}
      <div
        ref={(el) => carouselRevealRef(el as HTMLElement | null)}
        className="w-[100vw] relative left-1/2 -translate-x-1/2 md:w-full md:left-auto md:translate-x-0 flex justify-center"
      >
        <div className="flex flex-col gap-tile relative group/carousel w-full max-w-[100vw]">
          {/* Пустой фоновый блок — не анимируется (issue 6) */}
          <div
            className="glass-panel p-fluid-xl min-h-[clamp(100px,25svh,300px)] w-auto flex-grow mx-[var(--pad-panel-x,24px)] md:mx-0"
            style={{ border: "1px solid var(--help)" }}
          ></div>

          <div className="absolute top-[clamp(360px,62.5svh,900px)] -translate-y-1/2 left-[var(--pad-panel-x,24px)] right-[var(--pad-panel-x,24px)] h-0 flex justify-between items-center z-30 md:hidden pointer-events-none">
            <div className="relative translate-x-[-50%]">
              <button
                onClick={() => {
                  if (carouselRef.current) {
                    if (currentIndex <= 1) {
                      setCanScrollLeft(false);
                    }
                    const prevIndex = Math.max(currentIndex - 1, 0);
                    const targetCard = carouselRef.current.children[
                      prevIndex
                    ] as HTMLElement;
                    const firstCard = carouselRef.current
                      .children[0] as HTMLElement;
                    if (targetCard && firstCard) {
                      const targetScroll =
                        targetCard.offsetLeft - firstCard.offsetLeft;
                      carouselRef.current.scrollTo({
                        left: targetScroll,
                        behavior: "smooth",
                      });
                    }
                  }
                }}
                disabled={!canScrollLeft}
                className={`w-10 h-10 rounded-full border border-border/50 flex items-center justify-center text-foreground bg-background/80 backdrop-blur-md active:bg-accent transition-all pointer-events-auto shadow-sm shrink-0 ${canScrollLeft ? "opacity-100 visible" : "opacity-0 invisible"}`}
                aria-label="Предыдущий специалист"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>

              <button
                onClick={() => {
                  if (carouselRef.current) {
                    setCanScrollLeft(false);
                    carouselRef.current.scrollTo({
                      left: 0,
                      behavior: "smooth",
                    });
                  }
                }}
                disabled={currentIndex < 2}
                className={`absolute top-[calc(100%+8px)] left-0 w-10 h-10 rounded-full border border-border/50 flex items-center justify-center text-foreground bg-background/80 backdrop-blur-md active:bg-accent transition-all duration-300 pointer-events-auto shadow-sm shrink-0 ${currentIndex >= 2 ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}
                aria-label="В начало"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m11 17-5-5 5-5" />
                  <path d="m18 17-5-5 5-5" />
                </svg>
              </button>
            </div>

            <button
              onClick={() => {
                if (carouselRef.current) {
                  const totalCards = carouselRef.current.children.length;
                  if (currentIndex >= totalCards - 2) {
                    setCanScrollRight(false);
                  }
                  const nextIndex = Math.min(currentIndex + 1, totalCards - 1);
                  const targetCard = carouselRef.current.children[
                    nextIndex
                  ] as HTMLElement;
                  const firstCard = carouselRef.current
                    .children[0] as HTMLElement;
                  if (targetCard && firstCard) {
                    const targetScroll =
                      targetCard.offsetLeft - firstCard.offsetLeft;
                    carouselRef.current.scrollTo({
                      left: targetScroll,
                      behavior: "smooth",
                    });
                  }
                }
              }}
              disabled={!canScrollRight}
              className={`w-10 h-10 rounded-full border border-border/50 flex items-center justify-center text-foreground bg-background/80 backdrop-blur-md active:bg-accent transition-all pointer-events-auto shadow-sm shrink-0 translate-x-[50%] ${canScrollRight ? "opacity-100 visible" : "opacity-0 invisible"}`}
              aria-label="Следующий специалист"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>

          <div
            ref={carouselRef}
            id="specialists-carousel"
            onScroll={handleScroll}
            className="flex w-full overflow-x-auto snap-x snap-mandatory no-scrollbar gap-fluid-md md:flex-col md:overflow-visible md:snap-none md:gap-tile xl:grid xl:grid-cols-2 scroll-smooth px-[7.5vw] scroll-px-[7.5vw] md:px-0 md:scroll-px-0 snap-center"
          >
            {specialistsToShow.map((spec) => (
              <Link
                href={`/specialists/${spec.slug || spec.id}`}
                prefetch={true}
                key={spec.id}
                ref={(el) => { cardsRef.current[spec.id] = el; }}
                className="block bg-white border border-[var(--help)] rounded-[var(--fluid-radius-lg)] px-fluid-xl py-fluid-xl lg:py-fluid-md h-[clamp(440px,75svh,700px)] flex-shrink-0 snap-center relative overflow-hidden flex flex-col transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] w-[85vw] md:w-full md:h-[clamp(280px,calc((75svh-var(--gap-tile))/2),500px)] group [container-type:size]"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent h-1/2 mt-auto z-10 md:hidden pointer-events-none" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[150%] min-h-[60cqh] h-[70%] md:left-0 md:translate-x-0 md:w-[40%] md:h-[110%] md:min-h-0 z-10 pointer-events-none transition-all duration-500">
                  <Image
                    src={specImage}
                    alt=""
                    fill
                    className="object-contain object-bottom"
                    sizes="(max-width: 768px) 90vw, 40vw"
                    priority
                  />
                </div>
                <div className="flex-1 flex flex-col justify-between h-full relative z-20 md:ml-[35%]">
                  <div className="flex flex-col gap-fluid-sm">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
                      <div className="flex flex-col gap-1 text-left">
                        <h3 className="heading-specialist-name">{spec.name}</h3>
                        <p className="text-caption whitespace-normal leading-tight">{spec.qualification}</p>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <span className="text-caption text-foreground/80">Опыт работы: </span>
                        <span className="font-content font-medium text-fluid-base text-foreground">{spec.experience}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-fluid-sm">
                      {spec.approaches.map((approach, idx) => (
                        <span key={idx} className="h-[24px] rounded-full border border-[var(--help)] bg-transparent text-[var(--help)] px-2.5 flex items-center font-content text-[9px] uppercase tracking-wider select-none whitespace-nowrap">
                          {approach}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-fluid-sm border-t border-border/50 pt-fluid-sm w-full mt-auto">
                    <div className="flex flex-col text-left">
                      <span className="text-caption">Консультация</span>
                      <span className="heading-price">от {spec.priceFrom}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 mt-fluid-sm sm:mt-0">
                      <button className="btn-solid !h-[38px] !px-4 !text-[12px]">Подробнее</button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          scrollToContactFormWith(spec.name ?? "");
                        }}
                        className="btn-solid !h-[38px] !px-4 !text-[12px]"
                      >
                        Записаться
                      </button>
                    </div>
                  </div>
                </div>
              </Link>
            ))}

            {selectedTags.length === 0 &&
              visibleCount < specialists.length && (
                <div
                  onClick={() => setVisibleCount((prev) => prev + 5)}
                  className="bg-white border border-[var(--help)] rounded-[var(--fluid-radius-lg)] p-0 w-[85vw] md:w-full md:h-[clamp(280px,calc((75svh-var(--gap-tile))/2),500px)] h-[clamp(440px,75svh,700px)] flex-shrink-0 snap-center relative flex flex-col items-center justify-center lg:hover:bg-help transition-colors cursor-pointer group overflow-hidden"
                >
                  <div className="flex items-center justify-center w-16 h-16 rounded-full border border-border mb-4 group-hover:scale-110 transition-transform lg:group-hover:border-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-help lg:group-hover:text-white transition-colors"
                    >
                      <path d="M5 12h14" />
                      <path d="M12 5v14" />
                    </svg>
                  </div>
                  <h3 className="text-fluid-lg font-heading text-foreground lg:group-hover:text-white transition-colors">
                    Показать еще
                  </h3>
                </div>
              )}

            {selectedTags.length > 0 && filteredSpecialists.length >= 1 && (
              <div
                className={`bg-white border border-[var(--help)] rounded-[var(--fluid-radius-lg)] p-0 w-[85vw] md:w-full md:h-[clamp(280px,calc((75svh-var(--gap-tile))/2),500px)] h-[clamp(440px,75svh,700px)] flex-shrink-0 snap-center relative flex flex-col md:flex-row items-stretch justify-center overflow-hidden ${filteredSpecialists.length % 2 === 0 ? "xl:col-span-full" : ""}`}
              >
                <div
                  onClick={() => {
                    setSelectedTags([]);
                    if (carouselRef.current) {
                      carouselRef.current.scrollTo({
                        left: 0,
                        behavior: "smooth",
                      });
                    }
                  }}
                  className="flex-1 flex flex-col items-center justify-center text-center p-fluid-md lg:hover:bg-help transition-colors cursor-pointer group"
                >
                  <div className="flex items-center justify-center w-16 h-16 rounded-full border border-border mb-4 group-hover:scale-110 transition-transform lg:group-hover:border-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-help lg:group-hover:text-white transition-colors"
                    >
                      <path d="M13.013 3H2l8 9.46V19l4 2v-8.54l.9-1.055" />
                      <path d="m22 3-5 5" />
                      <path d="m17 3 5 5" />
                    </svg>
                  </div>
                  <h3 className="text-fluid-lg font-heading text-foreground lg:group-hover:text-white transition-colors max-w-[20ch]">
                    Сбросить фильтры и посмотреть всех
                  </h3>
                </div>
                <div className="w-full h-px bg-border md:w-px md:h-full shrink-0" />
                <div
                  onClick={scrollToContactForm}
                  className="flex-1 flex flex-col items-center justify-center text-center p-fluid-md lg:hover:bg-help transition-colors cursor-pointer group"
                >
                  <div className="flex items-center justify-center w-16 h-16 rounded-full border border-border mb-4 group-hover:scale-110 transition-transform lg:group-hover:border-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-help lg:group-hover:text-white transition-colors"
                    >
                      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <path d="M12 17h.01" />
                    </svg>
                  </div>
                  <h3 className="text-fluid-lg font-heading text-foreground lg:group-hover:text-white transition-colors max-w-[15ch]">
                    Помочь с выбором
                  </h3>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

SpecialistSection.displayName = "SpecialistSection";
